import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowRight,
    ArrowLeft,
    CheckCircle2,
    XCircle,
    Volume2,
    Turtle,
    Mic,
    Sparkles,
    BookOpen,
    Target,
    RotateCcw,
    Trophy,
    StopCircle,
    Play,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ttsLanguageOf, voiceOf } from "@/lib/lang";
import { useDashboard } from "@/contexts/DashboardContext";

const STEPS = [
    { n: 1, key: "objectif", label: "Objectif" },
    { n: 2, key: "rappel", label: "Rappel actif" },
    { n: 3, key: "vocabulaire", label: "Nouveau vocabulaire" },
    { n: 4, key: "mnemotechnique", label: "Image mentale" },
    { n: 5, key: "ecoute", label: "Écoute" },
    { n: 6, key: "prononciation", label: "Prononciation" },
    { n: 7, key: "contexte", label: "Mise en contexte" },
    { n: 8, key: "production", label: "Production active" },
    { n: 9, key: "quiz", label: "Quiz adaptatif" },
    { n: 10, key: "bilan", label: "Bilan" },
];

export default function Lesson() {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [advancing, setAdvancing] = useState(false);
    const [error, setError] = useState(null);
    const bootstrapped = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (bootstrapped.current) return;
        bootstrapped.current = true;
        (async () => {
            setLoading(true);
            try {
                // Route intensive-mode learners to /intensive instead of the 10-step standard flow.
                try {
                    const prof = await api.get("/profile/me");
                    const intensity = Number(prof.data?.profile?.intensity_words || 25);
                    if (intensity >= 50) {
                        navigate("/intensive", { replace: true });
                        return;
                    }
                } catch (err) {
                    // Profile fetch is best-effort — proceed with the
                    // standard lesson on error. Log so an unexpected 5xx
                    // doesn't silently degrade users who should be routed
                    // to /intensive.
                    console.warn("[Lesson] intensity gate profile fetch failed:", err);
                }
                const { data } = await api.post("/lesson/today/start");
                setSession(data.session);
                if (data.resumed) toast.info("Reprise de votre leçon là où vous l'aviez laissée.");
            } catch (err) {
                setError(formatApiError(err));
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [navigate]);

    async function advance() {
        if (!session || advancing) return;
        if (session.current_step >= 10) return;
        setAdvancing(true);
        try {
            const { data } = await api.post(`/lesson/session/${session.session_id}/advance`);
            setSession(data.session);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAdvancing(false);
        }
    }

    async function complete() {
        if (!session) return;
        setAdvancing(true);
        try {
            const { data } = await api.post(`/lesson/session/${session.session_id}/complete`);
            setSession(data.session);
            toast.success("Leçon terminée. Bien joué.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAdvancing(false);
        }
    }

    if (loading) return <LoadingScreen text="Préparation de votre leçon…" />;

    if (error) {
        return (
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <h1 className="font-serif text-2xl">Impossible de démarrer la leçon</h1>
                <p className="mt-2 text-sm text-inkSoft">{error}</p>
                <button
                    onClick={() => navigate("/dashboard")}
                    className="mt-6 inline-flex items-center gap-2 text-sm text-terracotta"
                    data-testid="lesson-back-dashboard"
                >
                    <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
                </button>
            </div>
        );
    }

    if (!session) return null;

    const step = session.current_step;
    const progress = (step / 10) * 100;

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="lesson-page">
            {/* Header */}
            <header className="mb-8" data-testid="lesson-header">
                <div className="flex items-center gap-2 mb-3">
                    <button
                        onClick={() => navigate("/dashboard")}
                        className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1"
                        data-testid="lesson-back-dashboard"
                    >
                        <ArrowLeft className="w-4 h-4" /> Tableau de bord
                    </button>
                    <span className="text-inkSoft text-sm">·</span>
                    <span className="text-xs tracking-[0.24em] uppercase text-inkSoft" data-testid="lesson-day">
                        Jour {session.program_day} · {session.cefr}
                    </span>
                </div>
                <h1
                    className="font-serif text-3xl md:text-4xl leading-tight tracking-tight"
                    data-testid="lesson-title"
                >
                    {session.theme_fr}
                </h1>
                <p className="text-sm text-inkSoft mt-1">{session.skill_focus}</p>

                {/* Progress */}
                <div className="mt-6">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-[11px] tracking-[0.22em] uppercase text-inkSoft font-medium">
                            Étape {step} / 10 · {STEPS[step - 1]?.label}
                        </span>
                        <span className="text-xs text-inkSoft" data-testid="lesson-completed-count">
                            {session.completed_steps?.length || 0} terminée(s)
                        </span>
                    </div>
                    <div className="h-1 bg-lexiBorder rounded-full overflow-hidden">
                        <div
                            className="h-full bg-terracotta transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                            data-testid="lesson-progress"
                        />
                    </div>
                </div>
            </header>

            <div className="animate-fade-up" data-testid={`lesson-step-${step}`}>
                {step === 1 && <Step1 session={session} onNext={advance} advancing={advancing} />}
                {step === 2 && <Step2 session={session} setSession={setSession} onNext={advance} advancing={advancing} />}
                {step === 3 && <Step3 session={session} onNext={advance} advancing={advancing} />}
                {step === 4 && <Step4 session={session} onNext={advance} advancing={advancing} />}
                {step === 5 && <Step5 session={session} onNext={advance} advancing={advancing} />}
                {step === 6 && <Step6 session={session} setSession={setSession} onNext={advance} advancing={advancing} />}
                {step === 7 && <Step7 session={session} onNext={advance} advancing={advancing} />}
                {step === 8 && <Step8 session={session} setSession={setSession} onNext={advance} advancing={advancing} />}
                {step === 9 && <Step9 session={session} setSession={setSession} onNext={advance} onComplete={complete} advancing={advancing} />}
                {step === 10 && <Step10 session={session} onComplete={complete} advancing={advancing} />}
            </div>
        </div>
    );
}

function LoadingScreen({ text }) {
    return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="lesson-loading">
            <Loader2 className="w-8 h-8 text-terracotta animate-spin" />
            <p className="mt-4 text-sm text-inkSoft">{text}</p>
        </div>
    );
}

function ContinueButton({ onClick, disabled, label = "Continuer", testId = "step-continue" }) {
    return (
        <div className="mt-8 flex justify-end">
            <button
                onClick={onClick}
                disabled={disabled}
                data-testid={testId}
                className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
            >
                {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {label}
            </button>
        </div>
    );
}

/* ------------------------- STEP 1: OBJECTIF ------------------------------- */

function Step1({ session, onNext, advancing }) {
    const c = session.content?.["1"] || {};
    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-8 md:p-10" data-testid="step1-objective-card">
            <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">Objectif</p>
            <Target className="w-6 h-6 text-terracotta mb-4" strokeWidth={1.75} />
            <p className="font-serif text-xl md:text-2xl leading-snug" data-testid="step1-objective-text">
                {c.objective}
            </p>
            <p className="mt-4 text-sm text-inkSoft">
                Objectif personnel : <span className="italic">{c.goal || "conversation"}</span>. Prêt·e ?
            </p>
            <ContinueButton onClick={onNext} disabled={advancing} testId="step1-continue" />
        </div>
    );
}

/* ------------------------- STEP 2: RAPPEL --------------------------------- */

function Step2({ session, setSession, onNext, advancing }) {
    const c = session.content?.["2"] || {};
    const questions = c.questions || [];
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [results, setResults] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    if (questions.length === 0) {
        return (
            <div className="bg-white border border-lexiBorder rounded-2xl p-8" data-testid="step2-empty">
                <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">Rappel actif</p>
                <p className="font-serif text-xl">Première leçon — rien à réviser encore.</p>
                <p className="text-sm text-inkSoft mt-2">
                    {c.empty_reason || "Nous démarrons directement le nouveau vocabulaire."}
                </p>
                <ContinueButton onClick={onNext} disabled={advancing} label="Découvrir le vocabulaire" testId="step2-continue" />
            </div>
        );
    }

    async function submit() {
        setSubmitting(true);
        try {
            const payload = { answers: questions.map((q) => ({ question_id: q.id, choice_index: answers[q.id] ?? -1 })) };
            const { data } = await api.post(`/lesson/session/${session.session_id}/recall`, payload);
            setResults(data.results);
            setSubmitted(true);
            setSession(data.session);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-10" data-testid="step2-recall-card">
            <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">Rappel actif</p>
            <h2 className="font-serif text-2xl">Que retenez-vous des sessions précédentes ?</h2>
            <p className="text-sm text-inkSoft mt-1">Répondez sans consulter — la correction viendra après.</p>

            <div className="mt-6 space-y-6">
                {questions.map((q, qi) => {
                    const chosen = answers[q.id];
                    const result = results?.find((r) => r.question_id === q.id);
                    return (
                        <div key={q.id} data-testid={`recall-q-${qi}`}>
                            <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">Question {qi + 1}</p>
                            <p className="font-serif text-lg mt-1">
                                <span className="italic">{q.prompt_target}</span>
                                <span className="text-inkSoft"> — {q.prompt_fr}</span>
                            </p>
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {q.choices.map((c, i) => {
                                    const isChosen = chosen === i;
                                    const correctIdx = result?.correct_index != null ? Number(result.correct_index) : null;
                                    const showCorrect = submitted && correctIdx !== null && i === correctIdx;
                                    const showWrong = submitted && isChosen && correctIdx !== null && i !== correctIdx;
                                    return (
                                        <button
                                            key={i}
                                            disabled={submitted}
                                            onClick={() => setAnswers({ ...answers, [q.id]: i })}
                                            data-testid={`recall-q-${qi}-choice-${i}`}
                                            className={`text-left px-4 py-2.5 rounded-xl border transition-colors ${
                                                showCorrect
                                                    ? "bg-emerald text-white border-emerald"
                                                    : showWrong
                                                      ? "bg-terracotta/10 border-terracotta/40 text-ink"
                                                      : isChosen
                                                        ? "bg-ink text-white border-ink"
                                                        : "bg-paper border-lexiBorder hover:bg-paperAlt"
                                            }`}
                                        >
                                            {c}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {!submitted ? (
                <div className="mt-8 flex justify-end">
                    <button
                        onClick={submit}
                        disabled={submitting || Object.keys(answers).length < questions.length}
                        data-testid="recall-submit"
                        className="inline-flex items-center gap-2 bg-ink hover:bg-emerald text-white rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Valider mes réponses
                    </button>
                </div>
            ) : (
                <ContinueButton onClick={onNext} disabled={advancing} label="Continuer vers le vocabulaire" testId="step2-continue" />
            )}
        </div>
    );
}

/* ------------------------- STEP 3: VOCABULAIRE ---------------------------- */

function Step3({ session, onNext, advancing }) {
    const c = session.content?.["3"] || {};
    const vocab = c.vocabulary || [];
    return (
        <div className="space-y-4" data-testid="step3-vocab-card">
            {c.intensity_note && (
                <div
                    className="bg-terracotta/8 border border-terracotta/30 rounded-2xl px-5 py-3 text-sm text-ink"
                    data-testid="intensity-note"
                >
                    {c.intensity_note}
                </div>
            )}
            <p className="text-xs tracking-[0.24em] uppercase text-inkSoft">Nouveau vocabulaire · {vocab.length} mots</p>
            {vocab.map((v, i) => (
                <VocabRow key={v.word_id || i} v={v} language={session.language_fr} index={i} />
            ))}
            <ContinueButton onClick={onNext} disabled={advancing} label="Voir l'image mentale" testId="step3-continue" />
        </div>
    );
}

function VocabRow({ v, language, index }) {
    const { data: dash } = useDashboard();
    const voice = voiceOf(dash?.profile);
    const [busy, setBusy] = useState(null);
    async function play(text, speed) {
        setBusy(speed);
        try {
            const { data } = await api.post("/poc/tts", { text, language: languageFrToPocLabel(language), speed, voice });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await audio.play();
        } catch (err) {
            toast.error(`TTS : ${formatApiError(err)}`);
        } finally {
            setBusy(null);
        }
    }
    const registerColor =
        v.register === "familier"
            ? "bg-terracotta/10 text-terracotta border-terracotta/30"
            : v.register === "formel"
              ? "bg-emerald/10 text-emerald border-emerald/30"
              : "bg-paperAlt text-inkSoft border-lexiBorder";
    return (
        <article
            className="bg-white border border-lexiBorder rounded-2xl p-5 md:p-6"
            data-testid={`vocab-row-${index}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft">{v.category}</p>
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight" data-testid={`vocab-row-${index}-word`}>
                        {v.word}
                    </h3>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
                        <span className="font-mono text-emerald">{v.ipa}</span>
                        <span className="text-inkSoft italic">{v.simplified}</span>
                        {v.register && (
                            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border ${registerColor}`}>
                                {v.register}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    <MiniAudioBtn onClick={() => play(v.word, "natural")} loading={busy === "natural"} icon={Volume2} testId={`vocab-${index}-audio-natural`} />
                    <MiniAudioBtn onClick={() => play(v.word, "slow")} loading={busy === "slow"} icon={Turtle} testId={`vocab-${index}-audio-slow`} />
                </div>
            </div>
            <p className="mt-3 text-sm">
                <span className="text-inkSoft text-xs tracking-[0.2em] uppercase mr-2">FR</span>
                {v.translation_fr}
            </p>
            {v.natural_sentence && (
                <div className="mt-3 bg-paper border border-lexiBorder rounded-xl p-4">
                    <p className="font-serif italic text-base leading-snug">« {v.natural_sentence} »</p>
                    <p className="text-sm text-inkSoft mt-1">{v.sentence_translation}</p>
                </div>
            )}
            {v.trap && (
                <div className="mt-3 rounded-xl p-3 bg-terracotta/8 border border-terracotta/30 text-sm">
                    <span className="text-terracotta font-semibold">Attention :</span> {v.trap}
                </div>
            )}
        </article>
    );
}

function MiniAudioBtn({ onClick, loading, icon: Icon, testId }) {
    return (
        <button
            onClick={onClick}
            disabled={loading}
            data-testid={testId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-ink text-white hover:bg-emerald disabled:opacity-60 transition-colors"
        >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
        </button>
    );
}

/* Map any target-language identifier to whatever /api/poc/tts expects. */
const languageFrToPocLabel = ttsLanguageOf;

/* ------------------------- STEP 4: MNEMOTECHNIQUE ------------------------- */

function Step4({ session, onNext, advancing }) {
    const c = session.content?.["4"] || {};
    // Try to load an illustration on mount. Cached b64 on the session is served
    // by the lazy endpoint if the background pre-compute already finished; if
    // generation failed for any reason, `image_base64` comes back null and we
    // silently keep the text-only card.
    const [image, setImage] = useState(c.scene_image_b64
        ? { b64: c.scene_image_b64, mime: c.scene_image_mime || "image/png" }
        : null);
    const [loadingImage, setLoadingImage] = useState(!image);

    useEffect(() => {
        if (image) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post(`/lesson/session/${session.session_id}/step4/illustration`);
                if (cancelled) return;
                if (data?.image_base64) {
                    setImage({ b64: data.image_base64, mime: data.mime_type || "image/png" });
                }
            } catch (err) {
                // Illustration is a nice-to-have — text-only fallback is
                // acceptable when the multimodal call fails. Log so a full
                // outage of the illustration pipeline is still visible.
                console.warn("[Lesson] step4 illustration fetch failed:", err);
            } finally {
                if (!cancelled) setLoadingImage(false);
            }
        })();
        return () => { cancelled = true; };
    }, [image, session.session_id]);

    return (
        <div className="bg-emerald text-white rounded-2xl p-8 md:p-10" data-testid="step4-mnemonic-card">
            <p className="text-[11px] tracking-[0.28em] uppercase text-white/70 mb-3">Image mentale</p>
            <Sparkles className="w-6 h-6 text-white mb-4" strokeWidth={1.75} />
            <p className="font-serif text-xl md:text-2xl leading-relaxed italic" data-testid="step4-scene-text">
                « {c.mnemonic_scene || "Prenez un instant pour visualiser les mots ensemble dans une petite scène."} »
            </p>

            {image ? (
                <div className="mt-6 rounded-2xl overflow-hidden bg-white/8 border border-white/20" data-testid="step4-scene-image">
                    <img
                        src={`data:${image.mime};base64,${image.b64}`}
                        alt="Illustration mnémotechnique de la scène"
                        className="w-full max-h-80 object-cover"
                        loading="lazy"
                    />
                </div>
            ) : loadingImage ? (
                <div className="mt-6 rounded-2xl border border-white/20 bg-white/5 flex items-center justify-center h-40" data-testid="step4-scene-image-loading">
                    <Loader2 className="w-5 h-5 animate-spin text-white/70" />
                </div>
            ) : null}

            <p className="mt-4 text-sm text-white/80">
                Fermez les yeux 10 secondes, laissez la scène se former en couleurs, en sons, en gestes.
            </p>
            <div className="mt-6 flex justify-end">
                <button
                    onClick={onNext}
                    disabled={advancing}
                    data-testid="step4-continue"
                    className="inline-flex items-center gap-2 bg-white text-emerald hover:bg-paperAlt rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
                >
                    Passer à l&apos;écoute <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

/* ------------------------- STEP 5: ECOUTE --------------------------------- */

function Step5({ session, onNext, advancing }) {
    const vocab = session.content?.["3"]?.vocabulary || [];
    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-10" data-testid="step5-listening-card">
            <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">Écoute</p>
            <h2 className="font-serif text-2xl">Écoutez chaque phrase, deux vitesses.</h2>
            <p className="text-sm text-inkSoft mt-1">D&apos;abord au tempo naturel, puis lentement pour saisir chaque syllabe.</p>
            <div className="mt-6 space-y-3">
                {vocab
                    .filter((v) => v.natural_sentence)
                    .slice(0, 8)
                    .map((v, i) => (
                        <ListeningRow key={i} v={v} language={session.language_fr} index={i} />
                    ))}
            </div>
            <ContinueButton onClick={onNext} disabled={advancing} label="Passer à la prononciation" testId="step5-continue" />
        </div>
    );
}

function ListeningRow({ v, language, index }) {
    const { data: dash } = useDashboard();
    const voice = voiceOf(dash?.profile);
    const [busy, setBusy] = useState(null);
    async function play(speed) {
        setBusy(speed);
        try {
            const { data } = await api.post("/poc/tts", { text: v.natural_sentence, language: languageFrToPocLabel(language), speed, voice });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await audio.play();
        } catch (err) {
            toast.error(`TTS : ${formatApiError(err)}`);
        } finally {
            setBusy(null);
        }
    }
    return (
        <div
            className="p-4 rounded-xl border border-lexiBorder bg-paper flex flex-wrap items-center justify-between gap-3"
            data-testid={`listening-row-${index}`}
        >
            <div className="min-w-0">
                <p className="font-serif italic leading-snug">« {v.natural_sentence} »</p>
                <p className="text-xs text-inkSoft mt-1">{v.sentence_translation}</p>
            </div>
            <div className="flex gap-2 shrink-0">
                <MiniAudioBtn onClick={() => play("natural")} loading={busy === "natural"} icon={Volume2} testId={`listen-${index}-natural`} />
                <MiniAudioBtn onClick={() => play("slow")} loading={busy === "slow"} icon={Turtle} testId={`listen-${index}-slow`} />
            </div>
        </div>
    );
}

/* ------------------------- STEP 6: PRONONCIATION -------------------------- */

function Step6({ session, setSession, onNext, advancing }) {
    const c = session.content?.["6"] || {};
    const sentences = c.sentences || [];
    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-10" data-testid="step6-pronunciation-card">
            <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">Prononciation</p>
            <h2 className="font-serif text-2xl">Enregistrez-vous, l&apos;IA vous écoute.</h2>
            <p className="text-sm text-inkSoft mt-1">Vous pouvez passer si vous n&apos;avez pas de micro maintenant.</p>
            {sentences.length === 0 && (
                <p className="mt-6 text-sm text-inkSoft italic">Aucune phrase disponible.</p>
            )}
            {sentences.map((s, i) => (
                <PronRow key={i} sentence={s} language={session.language_fr} />
            ))}
            <div className="mt-8 flex justify-between items-center">
                <button
                    onClick={onNext}
                    disabled={advancing}
                    data-testid="step6-skip"
                    className="text-sm text-inkSoft hover:text-ink underline underline-offset-4"
                >
                    Passer cette étape
                </button>
                <button
                    onClick={onNext}
                    disabled={advancing}
                    data-testid="step6-continue"
                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
                >
                    Continuer <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function PronRow({ sentence, language }) {
    const [status, setStatus] = useState("idle");
    const [analysis, setAnalysis] = useState(null);
    const [blob, setBlob] = useState(null);
    const [blobUrl, setBlobUrl] = useState(null);
    const [mr, setMr] = useState(null);

    async function start() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            const chunks = [];
            rec.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
            rec.onstop = () => {
                const b = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
                setBlob(b);
                setBlobUrl(URL.createObjectURL(b));
                stream.getTracks().forEach((t) => t.stop());
                setStatus("recorded");
            };
            rec.start();
            setMr(rec);
            setStatus("recording");
            setAnalysis(null);
        } catch {
            toast.error("Impossible d'accéder au micro. Autorisez-le dans le navigateur.");
        }
    }
    function stop() {
        if (mr && mr.state !== "inactive") mr.stop();
    }
    async function analyze() {
        if (!blob) return;
        setStatus("analyzing");
        try {
            const form = new FormData();
            form.append("audio", blob, "recording.webm");
            form.append("expected_sentence", sentence);
            form.append("language", languageFrToPocLabel(language));
            const { data } = await api.post("/poc/analyze-pronunciation", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAnalysis(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setStatus("recorded");
        }
    }

    return (
        <div className="mt-5 p-5 border border-lexiBorder rounded-xl bg-paper" data-testid="pron-row">
            <p className="font-serif italic">« {sentence} »</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
                {status !== "recording" && !blobUrl && (
                    <button
                        onClick={start}
                        data-testid="pron-start"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-4 py-2 text-sm active:scale-[0.98] transition-transform"
                    >
                        <Mic className="w-4 h-4" /> S&apos;enregistrer
                    </button>
                )}
                {status === "recording" && (
                    <button
                        onClick={stop}
                        data-testid="pron-stop"
                        className="inline-flex items-center gap-2 bg-ink text-white rounded-full px-4 py-2 text-sm"
                    >
                        <StopCircle className="w-4 h-4 animate-pulse" /> Arrêter
                    </button>
                )}
                {blobUrl && (
                    <>
                        <audio src={blobUrl} controls className="h-9" data-testid="pron-playback" />
                        <button
                            onClick={() => { setBlob(null); setBlobUrl(null); setAnalysis(null); setStatus("idle"); }}
                            className="text-xs text-inkSoft hover:text-ink inline-flex items-center gap-1"
                            data-testid="pron-reset"
                        >
                            <RotateCcw className="w-3 h-3" /> Recommencer
                        </button>
                        <button
                            onClick={analyze}
                            disabled={status === "analyzing"}
                            data-testid="pron-analyze"
                            className="inline-flex items-center gap-2 bg-emerald hover:bg-emeraldSoft text-white rounded-full px-4 py-2 text-sm disabled:opacity-60 active:scale-[0.98] transition-transform"
                        >
                            {status === "analyzing" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            Analyser
                        </button>
                    </>
                )}
            </div>
            {analysis && (
                <div className="mt-4 space-y-2 text-sm" data-testid="pron-result">
                    {analysis.status === "error" ? (
                        <p className="text-terracotta">{analysis.error_reason}</p>
                    ) : (
                        <>
                            <p className="text-xs text-inkSoft">
                                Entendu : <span className="font-mono">{analysis.heard_transcription}</span>
                            </p>
                            <p className="text-xs">
                                Score global : <span className="font-serif text-lg">{analysis.overall_score}</span>/100
                            </p>
                            <p className="text-xs italic">{analysis.targeted_tip}</p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/* ------------------------- STEP 7: CONTEXTE ------------------------------- */

function Step7({ session, onNext, advancing }) {
    const c = session.content?.["7"] || {};
    const lines = c.lines || [];
    const [revealed, setRevealed] = useState(0);
    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-10" data-testid="step7-context-card">
            <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">Mise en contexte</p>
            {c.setting_fr && <p className="text-sm text-inkSoft italic mb-4">{c.setting_fr}</p>}
            <div className="space-y-3">
                {lines.slice(0, revealed || lines.length).map((l, i) => (
                    <DialogueLine key={i} line={l} language={session.language_fr} index={i} />
                ))}
            </div>
            {revealed > 0 && revealed < lines.length && (
                <button
                    onClick={() => setRevealed((r) => Math.min(r + 1, lines.length))}
                    data-testid="dialogue-reveal-next"
                    className="mt-4 text-sm text-terracotta hover:underline"
                >
                    Afficher la réplique suivante
                </button>
            )}
            {revealed === 0 && lines.length > 0 && (
                <button
                    onClick={() => setRevealed(1)}
                    className="mt-4 text-sm text-terracotta hover:underline"
                    data-testid="dialogue-reveal-first"
                >
                    Révéler ligne par ligne (facultatif)
                </button>
            )}
            <ContinueButton onClick={onNext} disabled={advancing} label="Passer à la production" testId="step7-continue" />
        </div>
    );
}

function DialogueLine({ line, language, index }) {
    const { data: dash } = useDashboard();
    const voice = voiceOf(dash?.profile);
    const [busy, setBusy] = useState(false);
    async function play() {
        setBusy(true);
        try {
            const { data } = await api.post("/poc/tts", { text: line.text, language: languageFrToPocLabel(language), speed: "natural", voice });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await audio.play();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }
    return (
        <div className="p-4 border border-lexiBorder rounded-xl bg-paper" data-testid={`dialogue-line-${index}`}>
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs tracking-[0.22em] uppercase text-inkSoft">{line.speaker}</span>
                <MiniAudioBtn onClick={play} loading={busy} icon={Play} testId={`dialogue-play-${index}`} />
            </div>
            <p className="font-serif text-lg italic mt-1">« {line.text} »</p>
            <p className="text-xs text-inkSoft mt-1">{line.translation_fr}</p>
        </div>
    );
}

/* ------------------------- STEP 8: PRODUCTION ----------------------------- */

function Step8({ session, setSession, onNext, advancing }) {
    const c = session.content?.["8"] || {};
    const exercises = c.exercises || [];
    return (
        <div className="space-y-4" data-testid="step8-production-card">
            <p className="text-xs tracking-[0.24em] uppercase text-inkSoft">Production active</p>
            <h2 className="font-serif text-2xl">À vous d&apos;écrire — l&apos;IA corrige.</h2>
            {exercises.map((ex, i) => (
                <ProductionExercise
                    key={ex.id}
                    exercise={ex}
                    index={i}
                    sessionId={session.session_id}
                    setSession={setSession}
                />
            ))}
            <ContinueButton onClick={onNext} disabled={advancing} label="Passer au quiz" testId="step8-continue" />
        </div>
    );
}

function ProductionExercise({ exercise, index, sessionId, setSession }) {
    const [answer, setAnswer] = useState("");
    const [evaluation, setEvaluation] = useState(exercise.answers?.[exercise.answers.length - 1]?.eval || null);
    const [submitting, setSubmitting] = useState(false);

    async function submit() {
        if (!answer.trim()) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(`/lesson/session/${sessionId}/production`, {
                exercise_id: exercise.id,
                answer_text: answer.trim(),
            });
            setEvaluation(data.evaluation);
            setSession(data.session);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-6" data-testid={`prod-ex-${index}`}>
            <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft">Exercice {index + 1} · {exercise.type}</p>
            <h3 className="font-serif text-lg mt-1">{exercise.instruction_fr}</h3>
            {exercise.prompt && (
                <p className="mt-2 font-serif italic text-base">« {exercise.prompt} »</p>
            )}
            {exercise.guidance_fr && <p className="text-xs text-inkSoft mt-1">{exercise.guidance_fr}</p>}
            <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Écrivez votre réponse dans la langue cible…"
                data-testid={`prod-ex-${index}-input`}
                className="mt-3 w-full rounded-xl border border-lexiBorder bg-paper p-3 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
            <div className="mt-3 flex justify-end">
                <button
                    onClick={submit}
                    disabled={submitting || !answer.trim()}
                    data-testid={`prod-ex-${index}-submit`}
                    className="inline-flex items-center gap-2 bg-ink hover:bg-emerald text-white rounded-full px-5 py-2 text-sm disabled:opacity-60 active:scale-[0.98] transition-transform"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Soumettre
                </button>
            </div>
            {evaluation && (
                <div
                    className={`mt-4 rounded-xl p-4 text-sm ${
                        evaluation.correct ? "bg-emerald/10 border border-emerald/30" : "bg-terracotta/8 border border-terracotta/30"
                    }`}
                    data-testid={`prod-ex-${index}-eval`}
                >
                    <div className="flex items-center gap-2">
                        {evaluation.correct ? <CheckCircle2 className="w-4 h-4 text-emerald" /> : <XCircle className="w-4 h-4 text-terracotta" />}
                        <span className="text-xs font-medium">
                            Note : {evaluation.note}/5
                        </span>
                    </div>
                    {evaluation.corrected_version && (
                        <p className="mt-1 font-serif italic">« {evaluation.corrected_version} »</p>
                    )}
                    <p className="mt-1 text-xs text-inkSoft">{evaluation.explanation_fr}</p>
                    {evaluation.tip_fr && <p className="mt-1 text-xs italic">{evaluation.tip_fr}</p>}
                </div>
            )}
        </div>
    );
}

/* ------------------------- STEP 9: QUIZ ----------------------------------- */

function Step9({ session, setSession, onNext, onComplete, advancing }) {
    const c = session.content?.["9"] || {};
    const questions = c.questions || [];
    const [idx, setIdx] = useState(() => questions.findIndex((q) => q.answered == null));
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const current = questions[idx];
    const allAnswered = questions.every((q) => q.answered != null);

    async function submit(choiceIndex) {
        if (!current || busy) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/lesson/session/${session.session_id}/quiz`, {
                question_id: current.id,
                choice_index: choiceIndex,
            });
            setSession(data.session);
            setLastResult(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }
    function next() {
        setLastResult(null);
        if (idx < questions.length - 1) setIdx(idx + 1);
    }
    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-10" data-testid="step9-quiz-card">
            <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">
                Quiz adaptatif · {Math.min(idx + 1, questions.length)}/{questions.length}
            </p>
            {!current && (
                <p className="font-serif text-2xl">Toutes les questions ont été traitées.</p>
            )}
            {current && (
                <>
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                        {current.prompt_fr || "Question"}
                    </p>
                    {current.prompt_target && (
                        <p className="font-serif text-xl italic mt-1">« {current.prompt_target} »</p>
                    )}
                    <div className="mt-6 space-y-2">
                        {current.choices.map((ch, i) => {
                            const answered = lastResult && lastResult.session?.content?.["9"]?.questions?.[idx]?.answered;
                            const correctIdx = lastResult ? Number(lastResult.correct_index) : null;
                            const showCorrect = lastResult && correctIdx !== null && i === correctIdx;
                            const showWrong = answered && Number(answered.choice_index) === i && !answered.correct;
                            return (
                                <button
                                    key={i}
                                    disabled={busy || Boolean(lastResult)}
                                    onClick={() => submit(i)}
                                    data-testid={`quiz-choice-${idx}-${i}`}
                                    className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                                        showCorrect
                                            ? "bg-emerald text-white border-emerald"
                                            : showWrong
                                              ? "bg-terracotta/10 border-terracotta/40"
                                              : "bg-paper border-lexiBorder hover:bg-paperAlt"
                                    }`}
                                >
                                    <span className="font-mono text-xs text-inkSoft mr-3">
                                        {String.fromCharCode(65 + i)}
                                    </span>
                                    {ch}
                                </button>
                            );
                        })}
                    </div>
                    {lastResult && (
                        <div className="mt-4 p-3 rounded-xl bg-paper border border-lexiBorder text-sm" data-testid="quiz-explanation">
                            <p>
                                <strong>{lastResult.correct ? "Bien vu." : "Presque —"}</strong>{" "}
                                {lastResult.explanation_fr}
                            </p>
                            <div className="mt-3 flex justify-end">
                                {idx < questions.length - 1 ? (
                                    <button
                                        onClick={next}
                                        data-testid="quiz-next"
                                        className="inline-flex items-center gap-2 bg-ink hover:bg-emerald text-white rounded-full px-5 py-2 text-sm active:scale-[0.98] transition-transform"
                                    >
                                        Question suivante <ArrowRight className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <button
                                        onClick={onNext}
                                        data-testid="quiz-to-summary"
                                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2 text-sm active:scale-[0.98] transition-transform"
                                    >
                                        Voir le bilan <ArrowRight className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/* ------------------------- STEP 10: BILAN --------------------------------- */

function Step10({ session, onComplete, advancing }) {
    const c = session.content?.["10"] || {};
    const [done, setDone] = useState(!!session.completed_at);
    const navigate = useNavigate();

    async function finalize() {
        await onComplete();
        setDone(true);
    }

    return (
        <div className="space-y-4" data-testid="step10-summary-card">
            <div className="bg-emerald text-white rounded-2xl p-8 md:p-10">
                <p className="text-[11px] tracking-[0.28em] uppercase text-white/70 mb-3">Bilan</p>
                <Trophy className="w-6 h-6 text-white mb-3" strokeWidth={1.75} />
                <h2 className="font-serif text-2xl md:text-3xl leading-tight">
                    Score de la séance : <span className="italic">{c.score ?? 0}/100</span>
                </h2>
                {c.summary_fr && <p className="mt-4 leading-relaxed">{c.summary_fr}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-lexiBorder rounded-2xl p-5" data-testid="summary-acquired">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-2">Mots acquis</p>
                    <p className="font-serif text-xl">
                        {(c.acquired_words || []).length ? (c.acquired_words || []).join(", ") : "—"}
                    </p>
                </div>
                <div className="bg-white border border-lexiBorder rounded-2xl p-5" data-testid="summary-fragile">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-2">Mots fragiles</p>
                    <p className="font-serif text-xl">
                        {(c.fragile_words || []).length ? (c.fragile_words || []).join(", ") : "Aucun — beau travail."}
                    </p>
                </div>
            </div>

            {c.main_errors_fr?.length > 0 && (
                <div className="bg-white border border-lexiBorder rounded-2xl p-5" data-testid="summary-errors">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-2">Points à travailler</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                        {c.main_errors_fr.map((e, i) => (
                            <li key={i}>{e}</li>
                        ))}
                    </ul>
                </div>
            )}

            {c.daily_challenge_fr && (
                <div className="bg-terracotta/8 border border-terracotta/30 rounded-2xl p-5" data-testid="summary-challenge">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-terracotta font-semibold mb-2">
                        Défi du jour
                    </p>
                    <p className="font-serif text-lg">{c.daily_challenge_fr}</p>
                </div>
            )}

            {c.next_review && (
                <p className="text-xs text-inkSoft" data-testid="summary-next-review">
                    Prochaine révision : {new Date(c.next_review).toLocaleString("fr-FR")}
                </p>
            )}

            <div className="mt-6 flex flex-wrap gap-3 justify-end">
                {!done && (
                    <button
                        onClick={finalize}
                        disabled={advancing}
                        data-testid="lesson-complete-btn"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
                    >
                        {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Marquer comme terminé
                    </button>
                )}
                {done && (
                    <button
                        onClick={() => navigate("/dashboard")}
                        data-testid="lesson-return-dashboard"
                        className="inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-6 py-3 text-sm font-medium transition-colors"
                    >
                        Retour au tableau de bord <ArrowRight className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
